import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export function Footer() {
  const { t, language } = useLanguage();

  const productLinks = [
    { href: '/catalogue/cartes-nfc', label: language === 'fr' ? 'Cartes NFC' : 'NFC Cards' },
    { href: '/catalogue/stickers-nfc', label: language === 'fr' ? 'Stickers NFC' : 'NFC Stickers' },
    { href: '/catalogue/porte-cles-nfc', label: language === 'fr' ? 'Porte-clés NFC' : 'NFC Keychains' },
    { href: '/catalogue/plaques-nfc', label: language === 'fr' ? 'Plaques NFC' : 'NFC Plates' },
    { href: '/catalogue/bracelets-nfc', label: language === 'fr' ? 'Bracelets NFC' : 'NFC Bracelets' },
  ];

  const useCaseLinks = [
    { href: '/cas-usage/hotellerie', label: t('sector.hotel') },
    { href: '/cas-usage/restauration', label: t('sector.restaurant') },
    { href: '/cas-usage/immobilier', label: t('sector.real_estate') },
    { href: '/cas-usage/evenementiel', label: t('sector.event') },
    { href: '/cas-usage/commerce', label: t('sector.retail') },
    { href: '/cas-usage/entreprise', label: t('sector.corporate') },
  ];

  const legalLinks = [
    { href: '/mentions-legales', label: t('footer.legal') },
    { href: '/cgv', label: t('footer.cgv') },
    { href: '/confidentialite', label: t('footer.privacy') },
    { href: '/cookies', label: t('footer.cookies') },
  ];

  return (
    <footer className="bg-[#B91C1C] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <span className="font-bold text-2xl tracking-tight font-['Outfit']">
                LOGO<span className="text-[#DC2626]">TAG</span><sup className="text-[10px] align-super">®</sup>
              </span>
            </Link>
            <p className="text-sm text-gray-400 mb-6">
              {t('footer.tagline')}
            </p>
            <div className="space-y-3">
              <a
                href="mailto:info@logotag.com"
                className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#DC2626] transition-colors"
                data-testid="footer-email"
              >
                <Mail className="h-4 w-4" />
                info@logotag.com
              </a>
              <a
                href="tel:+33491481213"
                className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#DC2626] transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="h-4 w-4" />
                +33 4 91 48 12 13
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-widest mb-6">
              {language === 'fr' ? 'Produits' : 'Products'}
            </h4>
            <ul className="space-y-3">
              {productLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-[#DC2626] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Use Cases */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-widest mb-6">
              {t('nav.usecases')}
            </h4>
            <ul className="space-y-3">
              {useCaseLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-[#DC2626] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-widest mb-6">
              {language === 'fr' ? 'Informations' : 'Information'}
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-[#DC2626] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/contact"
                  className="text-sm text-gray-400 hover:text-[#DC2626] transition-colors"
                >
                  {t('footer.contact')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Logotag. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
            </p>
            <div className="flex items-center gap-4">
              <span className="text-xs text-gray-600 font-mono">
                NFC • NTAG • MIFARE
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
